import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';
import { RootState, ThunkServices } from '../../app/store';
import { appRoutesByName } from '../../containers/app/Routes';
import { PupilFormValues } from '../../components/customer-details-form/CustomerDetailsForm';
import { TIMEZONE } from '../../utils/constants';

export enum BookingStatusCode {
    Success = 0,
    PaymentError = 1,
    PupilError = 2,
    PurchaserError = 3,
    LessonError = 4,
}

export enum BookingResponseType {
    Success = 1,
    DetailsError = 2,
    LessonError = 3,
}

// State
export interface BookingState {
    bookingResponse?: endpoints.BookingResponse;
    bookingResponseType?: BookingResponseType;
    bookingProcessing: boolean;
}

export const initialBookingState: BookingState = {
    bookingResponse: undefined,
    bookingProcessing: false,
    bookingResponseType: undefined,
};

// Thunks
export const createBooking = createAsyncThunk<
    endpoints.BookingResponse,
    { navigate: (path: string) => void },
    { state: RootState; extra: ThunkServices }
>('booking/createBooking', async ({navigate}, { dispatch, getState, extra: { bookingService } }) => {
    const { userSelection, payment, products } = getState();
    const { pupilDetails, location, productId, lessonTime } = userSelection;
    const realexResponse = payment.paymentResponse?.responseObj as any;

    if (pupilDetails && payment && location && lessonTime && productId && realexResponse) {
        const product = products.availableProducts[productId];
        const details = {
            product,
            geoarea: location,
            lessonTime: lessonTime.toISOString(),
            ...mapformDataToBookingApi(pupilDetails),
        };

        dispatch(setBookingProcessing(true));
        try {
            const result = await bookingService.postBooking({ details, realexResponse });
            const bookingResponse = result.data;

            dispatch(
                setBookingResponse({
                    bookingResponse,
                    bookingResponseType: getBookingResponseType(bookingResponse.status.statusCode),
                })
            );
            dispatch(setBookingProcessing(false));
            navigate(appRoutesByName.confirmation.path);
            return bookingResponse;
        } catch (error) {
            const bookingResponse = (error as any)?.response?.data;
            const statusCode = bookingResponse?.status?.statusCode;

            dispatch(
                setBookingResponse({
                    bookingResponse,
                    bookingResponseType: getBookingResponseType(statusCode),
                })
            );
            dispatch(setBookingProcessing(false));
            dispatch<any>(push(appRoutesByName.confirmation.path));
            throw error;
        }
    } else {
        dispatch(
            setBookingResponse({
                bookingResponse: undefined,
                bookingResponseType: BookingResponseType.DetailsError,
            })
        );
        throw new Error('Unable to send booking info');
    }
});

// Slice
export const bookingSlice = createSlice({
    name: 'booking',
    initialState: initialBookingState,
    reducers: {
        setBookingProcessing: (state, action: PayloadAction<boolean>) => {
            state.bookingProcessing = action.payload;
        },
        setBookingResponse: (
            state,
            action: PayloadAction<{
                bookingResponse?: endpoints.BookingResponse;
                bookingResponseType: BookingResponseType;
            }>
        ) => {
            state.bookingResponse = action.payload.bookingResponse;
            state.bookingResponseType = action.payload.bookingResponseType;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createBooking.pending, (state) => {
                state.bookingProcessing = true;
            })
            .addCase(createBooking.fulfilled, (state) => {
                state.bookingProcessing = false;
            })
            .addCase(createBooking.rejected, (state) => {
                state.bookingProcessing = false;
            });
    },
});

// Actions
export const { setBookingProcessing, setBookingResponse } = bookingSlice.actions;

// Reducer
export const bookingReducer = bookingSlice.reducer;

// Helper Functions
export function mapformDataToBookingApi(formValues: PupilFormValues) {
    const { pupil, thirdParty } = formValues;
    return {
        pupil: pupil && {
            buildingName: pupil.addressLine1.trim(),
            streetAddress: pupil.addressLine2.trim(),
            locality: pupil.town.trim(),
            countyId: pupil.countyId,
            dateOfBirth: moment(
                `${pupil.dob.dobYear}-${pupil.dob.dobMonth}-${pupil.dob.dobDay}`
            )
                .tz(TIMEZONE)
                .format('YYYY-MM-DD'),
            emailAddress: pupil.email.trim(),
            emergencyPhoneNumber: pupil.emergencyNumber.trim(),
            firstName: pupil.firstName.trim(),
            surname: pupil.surname.trim(),
            marketConsent: pupil.marketingConsent,
            phoneNumber: pupil.mobile.trim(),
        },
        purchaser: thirdParty && {
            emailAddress: thirdParty.email.trim(),
            firstName: thirdParty.firstName?.trim(),
            surname: thirdParty.surname?.trim(),
        },
    };
}

export function getBookingResponseType(status?: number): BookingResponseType {
    if (
        status === BookingStatusCode.PaymentError ||
        status === BookingStatusCode.PupilError ||
        status === BookingStatusCode.PurchaserError
    ) {
        return BookingResponseType.DetailsError;
    } else if (status === BookingStatusCode.LessonError) {
        return BookingResponseType.LessonError;
    } else if (status === BookingStatusCode.Success) {
        return BookingResponseType.Success;
    } else {
        return BookingResponseType.DetailsError;
    }
}